import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { cssToMeonCoordinates, DEFAULT_BOX } from '../../utils/pdfCoordinates';

// Worker setup — served from public/ folder (copied from node_modules/pdfjs-dist/build/)
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const SIGNER_COLORS = [
  { border: 'border-blue-500', bg: 'bg-blue-500/15', label: 'bg-blue-500', text: 'text-blue-700' },
  { border: 'border-orange-500', bg: 'bg-orange-500/15', label: 'bg-orange-500', text: 'text-orange-700' },
  { border: 'border-purple-500', bg: 'bg-purple-500/15', label: 'bg-purple-500', text: 'text-purple-700' },
  { border: 'border-pink-500', bg: 'bg-pink-500/15', label: 'bg-pink-500', text: 'text-pink-700' },
  { border: 'border-cyan-500', bg: 'bg-cyan-500/15', label: 'bg-cyan-500', text: 'text-cyan-700' },
];

// ── Draggable Signature Box ──────────────────────────
const SignatureBox = ({ box, color, containerRef, onMove, onResize, onDelete }) => {
  const boxRef = useRef(null);
  const dragState = useRef(null);

  const handleMouseDown = useCallback((e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    dragState.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origLeft: box.left,
      origTop: box.top,
      origWidth: box.width,
      origHeight: box.height,
      containerW: rect.width,
      containerH: rect.height,
    };

    const handleMouseMove = (ev) => {
      const s = dragState.current;
      if (!s) return;
      const dx = ev.clientX - s.startX;
      const dy = ev.clientY - s.startY;

      if (s.mode === 'move') {
        const newLeft = Math.max(0, Math.min(s.origLeft + dx, s.containerW - s.origWidth));
        const newTop = Math.max(0, Math.min(s.origTop + dy, s.containerH - s.origHeight));
        onMove(newLeft, newTop);
      } else {
        const newW = Math.max(60, Math.min(s.origWidth + dx, s.containerW - s.origLeft));
        const newH = Math.max(30, Math.min(s.origHeight + dy, s.containerH - s.origTop));
        onResize(newW, newH);
      }
    };

    const handleMouseUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [box, containerRef, onMove, onResize]);

  return (
    <div
      ref={boxRef}
      className={`absolute border-2 ${color.border} ${color.bg} cursor-move select-none group`}
      style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
      onMouseDown={(e) => handleMouseDown(e, 'move')}
    >
      {/* Label */}
      <div className={`absolute -top-5 left-0 px-1.5 py-0.5 ${color.label} text-white text-[10px] font-bold rounded-t whitespace-nowrap`}>
        {box.signerLabel}
      </div>

      {/* Delete button */}
      <button
        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
        onMouseDown={(e) => { e.stopPropagation(); onDelete(); }}
      >
        &times;
      </button>

      {/* Resize handle */}
      <div
        className="absolute bottom-0 right-0 w-3 h-3 bg-gray-600 cursor-se-resize rounded-tl"
        onMouseDown={(e) => handleMouseDown(e, 'resize')}
      />
    </div>
  );
};

// ── Main Visual Mapper ──────────────────────────────
const VisualMapper = ({ formData, onComplete }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(null); // original PDF size in points
  const [renderedWidth, setRenderedWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // boxes: [{ id, signerIndex, signerLabel, pageNumber, left, top, width, height }]
  const [boxes, setBoxes] = useState([]);
  const [activeSignerIdx, setActiveSignerIdx] = useState(0);

  const signers = formData.signers || [];
  const isDigiSign = formData.signatureType === 'digisign';

  // ── Load PDF ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = atob(formData.fileBase64);
        const uint8 = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++) uint8[i] = data.charCodeAt(i);

        const pdf = await pdfjsLib.getDocument({ data: uint8 }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setCurrentPage(1);
      } catch (err) {
        if (!cancelled) setError('Failed to load PDF: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [formData.fileBase64]);

  // ── Render page on canvas ──────────────────────
  useEffect(() => {
    if (!pdfDocRef.current || currentPage < 1) return;
    let cancelled = false;

    const render = async () => {
      const pdf = pdfDocRef.current;
      const page = await pdf.getPage(currentPage);

      // Scale to fit container (max ~600px wide)
      const unscaled = page.getViewport({ scale: 1 });
      const containerW = containerRef.current?.parentElement?.clientWidth || 600;
      const targetW = Math.min(containerW - 32, 600);
      const scale = targetW / unscaled.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;

      if (!cancelled) {
        setPageSize({ width: unscaled.width, height: unscaled.height });
        setRenderedWidth(viewport.width);
      }
    };

    render();
    return () => { cancelled = true; };
  }, [currentPage, numPages]);

  // ── Box management ─────────────────────────────
  const addBox = useCallback(() => {
    // DigiSign: only one box per signer
    if (isDigiSign) {
      const existing = boxes.find(
        (b) => b.signerIndex === activeSignerIdx
      );
      if (existing) return;
    }

    const signer = signers[activeSignerIdx];
    setBoxes((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        signerIndex: activeSignerIdx,
        signerLabel: signer?.name || `Signer ${activeSignerIdx + 1}`,
        pageNumber: currentPage,
        left: 80,
        top: 80,
        width: DEFAULT_BOX.width,
        height: DEFAULT_BOX.height,
      },
    ]);
  }, [activeSignerIdx, currentPage, signers, boxes, isDigiSign]);

  const moveBox = useCallback((id, left, top) => {
    setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, left, top } : b)));
  }, []);

  const resizeBox = useCallback((id, width, height) => {
    setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, width, height } : b)));
  }, []);

  const deleteBox = useCallback((id) => {
    setBoxes((prev) => prev.filter((b) => b.id !== id));
  }, []);

  // ── Apply to all pages ─────────────────────────
  const applyToAllPages = useCallback(() => {
    if (numPages <= 1) return;
    // Get boxes on the current page for the active signer
    const currentBoxes = boxes.filter(
      (b) => b.pageNumber === currentPage && b.signerIndex === activeSignerIdx
    );
    if (currentBoxes.length === 0) return;

    setBoxes((prev) => {
      // Remove existing boxes for this signer on OTHER pages
      const kept = prev.filter(
        (b) => !(b.signerIndex === activeSignerIdx && b.pageNumber !== currentPage)
      );
      // Clone current page boxes to every other page
      const clones = [];
      for (let pg = 1; pg <= numPages; pg++) {
        if (pg === currentPage) continue;
        for (const box of currentBoxes) {
          clones.push({
            ...box,
            id: Date.now() + Math.random() + pg,
            pageNumber: pg,
          });
        }
      }
      return [...kept, ...clones];
    });
  }, [boxes, currentPage, activeSignerIdx, numPages]);

  // ── Save & Continue ────────────────────────────
  const handleSave = useCallback(() => {
    if (!pageSize || !renderedWidth) return;

    const coordinates = boxes.map((b) => {
      const meon = cssToMeonCoordinates(
        { left: b.left, top: b.top, width: b.width, height: b.height },
        pageSize,
        renderedWidth
      );
      return {
        signerIndex: b.signerIndex,
        page_number: b.pageNumber,
        ...meon,
      };
    });

    onComplete(coordinates);
  }, [boxes, pageSize, renderedWidth, onComplete]);

  // Boxes visible on this page
  const pageBoxes = boxes.filter((b) => b.pageNumber === currentPage);

  // Validation: every signer must have at least one box
  const allSignersHaveBoxes = signers.every((_, idx) =>
    boxes.some((b) => b.signerIndex === idx)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-7 h-7 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-600 py-8 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Signer selector (only if multi) */}
        {signers.length > 1 ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {signers.map((s, idx) => {
              const c = SIGNER_COLORS[idx % SIGNER_COLORS.length];
              return (
                <button
                  key={idx}
                  onClick={() => setActiveSignerIdx(idx)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all ${
                    activeSignerIdx === idx
                      ? `${c.border} ${c.bg} ${c.text}`
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {s.name || `Signer ${idx + 1}`}
                </button>
              );
            })}
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={addBox}
            className="px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
            </svg>
            Add Signature Box
          </button>
          {numPages > 1 && pageBoxes.length > 0 && (
            <button
              onClick={applyToAllPages}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Apply to All Pages
            </button>
          )}
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="bg-gray-100 rounded-xl p-4 flex justify-center">
        <div ref={containerRef} className="relative inline-block shadow-lg">
          <canvas ref={canvasRef} className="block rounded" />
          {pageBoxes.map((box) => {
            const c = SIGNER_COLORS[box.signerIndex % SIGNER_COLORS.length];
            return (
              <SignatureBox
                key={box.id}
                box={box}
                color={c}
                containerRef={containerRef}
                onMove={(left, top) => moveBox(box.id, left, top)}
                onResize={(w, h) => resizeBox(box.id, w, h)}
                onDelete={() => deleteBox(box.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Page navigation */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-xs text-gray-600 font-medium">
            Page {currentPage} of {numPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
            disabled={currentPage >= numPages}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Box summary */}
      {boxes.length > 0 && (
        <div className="text-xs text-gray-500 text-center">
          {boxes.length} signature {boxes.length === 1 ? 'box' : 'boxes'} placed
          {numPages > 1 && ` across ${new Set(boxes.map((b) => b.pageNumber)).size} page(s)`}
        </div>
      )}

      {/* Save & Continue */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={!allSignersHaveBoxes}
          className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Next: Review &amp; Send
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default VisualMapper;
